// LiveStreamAnimationGift.jsx
import React, {
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { LivestreamPlayerContext } from '../context/LivestreamPlayerContext.js';
import getAPNG from '../resource/getAPNG.js';
import PAGCanvas from '../component/PAGCanvas.jsx';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import trackMixpanel from '../resource/mixpanel.js';
import { EventTypes } from '../resource/trackingConstants.js';
import { livestream as livestreamDebug } from '../resource/debug.js';
import { startSentryLogCapture } from '../resource/sentry.js';
import { LOG_CAPTURE_LIVESTREAM_SEND_GIFT } from '../resource/sentryLogCaptureNames.js';

const log = livestreamDebug.extend('log:LiveStreamAnimationGift');
const errorLog = livestreamDebug.extend('error:LiveStreamAnimationGift');

function getContainedSize({
  childWidth,
  childHeight,
  parentWidth,
  parentHeight,
}) {
  const childRatio = childWidth / childHeight;
  const parentRatio = parentWidth / parentHeight;

  const isChildWiderThanParent = childRatio > parentRatio;

  return {
    width: isChildWiderThanParent ? parentWidth : parentHeight * childRatio,
    height: isChildWiderThanParent ? parentWidth / childRatio : parentHeight,
  };
}

export const LiveStreamAnimationGift = ({
  byteplusPlayerVideoWidth = 0,
  byteplusPlayerVideoHeight = 0,
  giftNoticeId = '',
  giftId = '',
  streamId = '',
  isSender = false,
  giftImage = '',
  pagImage = '',
  isPagGift = false,
  isStaticGift = false,
  shouldShowMainAsset = false,
  wrapperRef = EMPTY_OBJECT,
}) => {
  const canvasRef = useRef();
  const player = useContext(LivestreamPlayerContext)?.player;

  const handlePagPlay = useCallback(() => {
    if (!isSender) return;
    log(`gift [${giftId}] PAG playing`, { giftId, streamId, giftNoticeId });
    trackMixpanel({
      type: EventTypes.LIVESTREAM_GIFT_ANIMATION_PLAYED,
      payload: {
        giftId,
        streamId,
        animationType: 'pag',
      },
    });
  }, [giftNoticeId, giftId, streamId, isSender]);

  let byteplusPlayerDisplayWidth = 0,
    byteplusPlayerDisplayHeight = 0;

  if (wrapperRef.current) {
    const result = getContainedSize({
      childWidth: byteplusPlayerVideoWidth,
      childHeight: byteplusPlayerVideoHeight,
      parentWidth: wrapperRef.current.clientWidth,
      parentHeight: wrapperRef.current.clientHeight,
    });
    byteplusPlayerDisplayWidth = result.width;
    byteplusPlayerDisplayHeight = result.height;
  }

  // get video screen size without black bars
  const streamingScreenWidth =
    player?.getVideoElement?.().offsetWidth ||
    player?.currentWidth?.() ||
    byteplusPlayerDisplayWidth ||
    0;
  const streamingScreenHeight =
    player?.getVideoElement?.().offsetHeight ||
    player?.currentHeight?.() ||
    byteplusPlayerDisplayHeight ||
    0;

  const originalVideoWidth =
    player?.videoWidth?.() ||
    player?.getBitrateInfoListFor?.('video')?.[0]?.width ||
    byteplusPlayerVideoWidth ||
    0;
  const originalVideoHeight =
    player?.videoHeight?.() ||
    player?.getBitrateInfoListFor?.('video')?.[0]?.height ||
    byteplusPlayerVideoHeight ||
    0;

  const { width: actualVideoWidth, height: actualVideoHeight } =
    getContainedSize({
      parentHeight: streamingScreenHeight,
      parentWidth: streamingScreenWidth,
      childHeight: originalVideoHeight,
      childWidth: originalVideoWidth,
    });

  // Applied declaratively so a canvas mounted later still gets the bounds.
  // Only the bounds belong here: playAnimationGift() owns width / height.
  const giftCanvasStyle = useMemo(
    () => ({
      maxWidth: Number.isFinite(actualVideoWidth)
        ? `${actualVideoWidth}px`
        : undefined,
      maxHeight: Number.isFinite(actualVideoHeight)
        ? `${actualVideoHeight}px`
        : undefined,
    }),
    [actualVideoWidth, actualVideoHeight]
  );

  useEffect(() => {
    let player = null;
    let isCancelled = false;

    async function playAnimationGift() {
      const { isAPNG, apng } = await getAPNG({ imageSource: giftImage });
      if (!isAPNG) {
        errorLog(`gift [${giftId}] APNG decode failed: not APNG format`, {
          giftId,
          streamId,
          giftNoticeId,
        });
        return;
      }
      if (!canvasRef.current) {
        errorLog(`gift [${giftId}] APNG decode failed: canvas not available`, {
          giftId,
          streamId,
          giftNoticeId,
        });
        return;
      }
      apng.numPlays = 1;
      canvasRef.current.width = apng.width;
      canvasRef.current.style.width = `${apng.width / 2}px`;

      canvasRef.current.height = apng.height;
      canvasRef.current.style.height = `${apng.height / 2}px`;

      player = await apng.getPlayer(canvasRef.current.getContext('2d'));
      if (isCancelled) {
        player.stop();
        return;
      }
      player.play();
      if (isSender) {
        log(`gift [${giftId}] APNG playing`, {
          giftId,
          streamId,
          giftNoticeId,
        });
        trackMixpanel({
          type: EventTypes.LIVESTREAM_GIFT_ANIMATION_PLAYED,
          payload: {
            giftId,
            streamId,
            animationType: 'apng',
          },
        });
      }
    }

    // Only play APNG animation if it's not a PAG gift, and not the static
    // fallback (decoding a plain thumbnail as APNG always fails)
    if (giftImage && shouldShowMainAsset && !isPagGift && !isStaticGift) {
      playAnimationGift();
    }

    return () => {
      isCancelled = true;
      player?.stop();
      player = null;
    };
  }, [
    giftImage,
    giftNoticeId,
    giftId,
    streamId,
    isSender,
    shouldShowMainAsset,
    isPagGift,
    isStaticGift,
  ]);

  useEffect(() => {
    if (isSender) {
      startSentryLogCapture({ captureName: LOG_CAPTURE_LIVESTREAM_SEND_GIFT });
    }
  }, [isSender]);

  if ((!giftImage && !pagImage) || !shouldShowMainAsset) {
    const reason =
      !giftImage && !pagImage ? 'no image available' : 'asset not preloaded';
    log(`gift [${giftId}] animation skipped: ${reason}`, {
      giftId,
      streamId,
      giftNoticeId,
      hasGiftImage: !!giftImage,
      hasPagImage: !!pagImage,
      shouldShowMainAsset,
    });
    return null;
  }
  return (
    <StyledLiveStreamAnimationGift>
      {isPagGift && pagImage ? (
        <>
          <PAGCanvas
            key={`pag-${giftNoticeId}`}
            src={pagImage}
            shouldPlay={true}
            onPlay={handlePagPlay}
            repeat={1}
            className="pag-gift-animation"
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
        </>
      ) : isStaticGift ? (
        <StaticGiftImage src={giftImage} alt="" />
      ) : (
        <>
          <GiftCanvas ref={canvasRef} style={giftCanvasStyle} />
        </>
      )}
    </StyledLiveStreamAnimationGift>
  );
};

LiveStreamAnimationGift.propTypes = {
  byteplusPlayerVideoWidth: PropTypes.number,
  byteplusPlayerVideoHeight: PropTypes.number,
  giftNoticeId: PropTypes.string,
  giftId: PropTypes.string,
  streamId: PropTypes.string,
  isSender: PropTypes.bool,
  giftImage: PropTypes.string,
  pagImage: PropTypes.string,
  isPagGift: PropTypes.bool,
  isStaticGift: PropTypes.bool,
  shouldShowMainAsset: PropTypes.bool,
  wrapperRef: PropTypes.object,
};

const StyledLiveStreamAnimationGift = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const GiftCanvas = styled.canvas`
  object-fit: contain;
`;

const StaticGiftImage = styled.img`
  max-width: 50%;
  max-height: 50%;
  object-fit: contain;
`;

export default LiveStreamAnimationGift;
